import type { IsActiveMatchOptions } from "@angular/router";

/**
 * Config model for the data-driven side navigation.
 *
 * Items store raw route SEGMENTS (not pre-resolved links): the renderer resolves
 * them to company-scoped links via `NavRoutesService.route(...)`, so this config
 * stays static/declarative and reactivity to the selected company is automatic.
 */

/** One clickable link in the side navigation. */
export interface SidenavItem {
    /** Translation key used for both the label and the collapsed tooltip. */
    labelKey: string;
    /** PrimeIcons class without the leading `pi pi-`, e.g. `"envelope"`. */
    icon: string;
    /** Segments appended after the company id, e.g. `["super-user", "users"]`. */
    segments: (string | number)[];
    /**
     * Escape hatch for items that don't follow their group's segment prefix.
     * When set, these segments are used verbatim (still company-scoped) and
     * `segments` is ignored. Used for exchange-rates: `["home", "exchange-rates"]`.
     */
    absoluteSegments?: (string | number)[];
    /**
     * `routerLinkActive` matching options. Omit to use Angular's default
     * matching; only the main-menu items use `SUBSET_MATCH_OPTIONS`.
     */
    routerLinkActiveOptions?: IsActiveMatchOptions;
    /** Optional stable id for trackBy / test hooks. */
    id?: string;
}

/** A labelled list block: an optional subheading + a list of links. */
export interface SidenavSection {
    /** Translation key for the subheading. Omit for an unlabelled section. */
    subheadingKey?: string;
    items: SidenavItem[];
    /** Optional stable id for trackBy. */
    id?: string;
}

/**
 * A collapsible admin group (Super User / Developer): a toggle row that opens a
 * hover fly-out when collapsed and reveals inline sections when expanded.
 * Presentational config only — open/fly-out STATE lives in the parent view.
 */
export interface SidenavGroup {
    /** Stable key used by the parent for open-state + fly-out identity. */
    key: string;
    /** Translation key for the toggle label + fly-out title. */
    labelKey: string;
    /** PrimeIcons class without the leading `pi pi-`. */
    icon: string;
    sections: SidenavSection[];
}

/**
 * Active-match options for the top-level menu links: a link stays highlighted
 * while the user is anywhere under its route (subset path matching).
 */
export const SUBSET_MATCH_OPTIONS: IsActiveMatchOptions = {
    paths: "subset",
    matrixParams: "ignored",
    queryParams: "ignored",
    fragment: "ignored",
};
